import ContentHeader from '../../components/UI/ContentHeader/ContentHeader';
import { MASTER_LINKS } from '../../constants/master-list-tabs';
import DrugList from './DrugList/DrugList';
import DrugService from '../../services/DrugService';
import { ReactQueryKeys } from '../../constants/react-query-keys';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { SweetAlertResult } from 'sweetalert2';
import SwalAlert, {
  firePreConfirmAlert,
} from '../../components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from '../../constants/roles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ListDrug } from '../../interfaces/list-drug';
import Spinner from 'components/UI/Spinner/Spinner';
import HospitalTotalLabels from 'components/UI/HospitalTotalLabels/HospitalTotalLabels';
import { drugActions } from 'store/slices/drug';
import { selectUser } from 'store/slices/auth';

/**
 * @description Drugs component that displays a list of drugs from the master catalog with their details.
 * Provides functionality for searching, pagination, and deletion of drugs with role-based access control.
 *
 * @SADtrace SAD-003-3.1, SAD-003-3.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} The rendered Drugs component
 */
const Drugs = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const user = useSelector(selectUser);
  // v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type
  const canManage = [ADMINISTRATION, AUTHOR].includes(user?.role || '');
  // v8 ignore stop -- WAIVER(MICRO)

  useEffect(() => {
    return () => {
      dispatch(drugActions.setPageIndex(0));
    };
  }, []);

  const {
    data: listDrugs,
    isLoading: isLoadingListDrugs,
    isFetching,
  } = useQuery({
    queryKey: [
      ReactQueryKeys.LIST_DRUGS,
      {
        columnFilters,
        pagination,
      },
    ],

    queryFn: () =>
      DrugService.get({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
        columnFilters: columnFilters,
      }),

    placeholderData: keepPreviousData,
  });

  /**
   * @description Updates search filters for the API call to filter drugs by name
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string} value - The search value to filter drugs by name
   *
   * @returns {void}
   */
  const handleUpdateFilter = (value: string) => {
    setColumnFilters([
      {
        id: 'name',
        value,
      },
    ]);
  };

  /**
   * @description Shows confirmation alert for deleting a drug record from the master catalog.
   * If confirmed, deletes the drug and updates the list
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {ListDrug} drug - The drug to be deleted
   *
   * @returns {void}
   */
  const handleDeleteShow = (drug: ListDrug) => {
    firePreConfirmAlert({
      title: t('drugs.messages.modal__delete__title'),
      html: t('drugs.messages.modal__delete__content', { name: drug.name }),
      preConfirm: () => {
        return DrugService.delete(String(drug.id))
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.LIST_DRUGS],
        });
        navigate('/drugs');
        toast.success(t('drugs.messages.delete__success'));
      }
    });
  };

  return (
    <>
      <ContentHeader links={MASTER_LINKS} />
      <div className="main-container with-tabs" data-testid="main-container">
        <div className="row">
          <div className="col-3">
            {isLoadingListDrugs ? (
              <Spinner isAbsolute />
            ) : (
              <>
                <HospitalTotalLabels
                  translationKey="drugs"
                  total={listDrugs?.data.totalConcentrations}
                />

                <DrugList
                  refetch={isLoadingListDrugs || isFetching}
                  canManage={canManage}
                  onDelete={handleDeleteShow}
                  updateFilter={handleUpdateFilter}
                  drugsResponse={listDrugs?.data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            )}
          </div>
          <div className="col-9 ps-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default Drugs;
